import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { noteName, DEFAULT_STRINGS } from '@/lib/pitch';

const MIN = 18;
const MAX = 76;

const StringRow = ({ idx, total, midi, onChange }) => (
  <div className="flex items-center gap-3">
    <span className="w-16 text-[10px] uppercase tracking-[0.2em] text-white/40">Corda {total - idx}</span>
    <button data-testid={`tuning-editor-string-${idx}-down`} aria-label="Nota più bassa" onClick={() => onChange(Math.max(MIN, midi - 1))} className="h-10 w-10 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5 active:scale-95 transition-transform"><Minus className="w-4 h-4" /></button>
    <span data-testid={`tuning-editor-string-${idx}-note`} className="w-14 text-center font-bebas text-2xl">{noteName(midi, false)}</span>
    <button data-testid={`tuning-editor-string-${idx}-up`} aria-label="Nota più alta" onClick={() => onChange(Math.min(MAX, midi + 1))} className="h-10 w-10 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5 active:scale-95 transition-transform"><Plus className="w-4 h-4" /></button>
  </div>
);

export const TuningEditor = ({ open, initial, counts, onSave, onDelete, onClose }) => {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState(DEFAULT_STRINGS[counts[0]]);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setNotes(initial?.notes || DEFAULT_STRINGS[counts[0]]);
  }, [open, initial, counts]);

  const setCount = (n) => setNotes(DEFAULT_STRINGS[n]);
  const valid = name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tuning-editor" className="bg-[#111112] border-white/10 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-bebas text-3xl tracking-wide">{initial ? 'Modifica accordatura' : 'Nuova accordatura'}</DialogTitle>
          <DialogDescription className="text-white/50">Dalla corda più bassa alla più alta.</DialogDescription>
        </DialogHeader>
        <input
          data-testid="tuning-editor-name"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome (es. My Open C)"
          className="h-11 rounded-xl bg-[#0A0A0A] border border-white/10 px-3 text-sm focus:outline-none focus:border-pink-400"
        />
        {counts.length > 1 && (
        <div className="grid grid-cols-3 gap-1.5">
          {counts.map((n) => (
            <button key={n} data-testid={`tuning-editor-count-${n}`} onClick={() => setCount(n)} className={`h-10 rounded-xl text-xs uppercase tracking-[0.15em] transition-colors ${notes.length === n ? 'bg-pink-400 text-black' : 'border border-white/10 text-white/70 hover:bg-white/5'}`}>{n} corde</button>
          ))}
        </div>
        )}
        <div className="flex flex-col gap-2">
          {notes.map((m, i) => (
            <StringRow key={i} idx={i} total={notes.length} midi={m} onChange={(v) => setNotes(notes.map((x, j) => (j === i ? v : x)))} />
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          {initial && (
            <button data-testid="tuning-editor-delete" onClick={() => onDelete(initial.id)} className="h-11 px-4 rounded-full border border-rose-400/40 text-rose-300 text-xs uppercase tracking-[0.2em] hover:bg-rose-500/10">Elimina</button>
          )}
          <button
            data-testid="tuning-editor-save"
            disabled={!valid}
            onClick={() => onSave({ id: initial?.id, name: name.trim(), notes })}
            className="flex-1 h-11 rounded-full bg-pink-400 text-black text-xs font-semibold uppercase tracking-[0.2em] hover:bg-pink-300 disabled:opacity-40 active:scale-95 transition-transform"
          >
            Salva
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
